import { Autocomplete, FormControl, TextField } from "@mui/material";
import cx from "classnames";
import type { Task } from "src/queries/tasks/tasks.types";
import TaskRunSelectorDefaultRenderOption, {
  type TaskRunSelectorOptionOnClickType,
} from "./components/TaskSelectorDefaultRenderOption/TaskRunSelectorDefaultRenderOption";
import { useTaskRunSelector } from "./TaskRunSelector.hooks";
import type { TaskRunSelectorProps } from "./TaskRunSelector.types";
import { filterOptions, isOptionEqualToValue } from "./TaskRunSelector.utils";

const TaskRunSelector = ({
  task,
  disabled,
  isLoading,
  formSx,
  autoFocus,
  renderOption = TaskRunSelectorDefaultRenderOption,
  ...props
}: TaskRunSelectorProps) => {
  const RenderOption = renderOption;
  const { minStartTime, totalDuration, onChange, slotProps } =
    useTaskRunSelector(props);

  return (
    <FormControl
      sx={formSx}
      size="small"
      className={cx({ "flex-none!": !task })} // minimize field's width if empty
    >
      <Autocomplete
        loading={isLoading}
        value={task || null}
        openOnFocus
        autoSelect={true}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            label={isLoading ? "Fetching tasks…" : "Task"}
            {...slotProps?.renderInput?.TextField}
          />
        )}
        disabled={isLoading || disabled}
        size="small"
        options={props.tasks ?? []}
        noOptionsText="No tasks for the selected pipeline run"
        includeInputInList={true}
        filterOptions={filterOptions}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionKey={(option) => option.task_id}
        getOptionLabel={() => task?.task_name ?? ""}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;

          return (
            <RenderOption
              key={key}
              option={option as Task}
              {...optionProps}
              minStartTime={minStartTime}
              totalDuration={totalDuration}
              onClick={
                slotProps?.renderOption?.onClick ||
                (optionProps.onClick as TaskRunSelectorOptionOnClickType)
              }
            />
          );
        }}
        {...props}
        onChange={onChange}
        slotProps={slotProps}
        data-test-id="task-run-selector"
      />
    </FormControl>
  );
};

export default TaskRunSelector;
